/**
 * AIConfigFeature - Frontend feature for AI configuration management
 *
 * Manages Anthropic API key for AI-powered features
 * (Interrogation, Chat AI, Dossier, Media Classification, etc.)
 */
import { BaseFeature } from '../../core/base-feature.js';

export class AIConfigFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.isConfigured = false;
        this.maskedKey = null;
        this.gameSampling = false;

        // Callbacks for UI updates
        this.configCallbacks = new Set();

        // Pending request/response tracking
        this.pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'ai-config',
            name: 'AI Configuration',
            version: 'v1.0',
            capabilities: ['get_config', 'configure', 'validate_key'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[AIConfig] Initializing frontend feature...');
    }

    /**
     * Handle incoming messages from desktop
     */
    async handleMessage(message) {
        const { action, payload, messageId, originalMessageId } = message;

        // Check if this is a response to a pending request
        // originalMessageId may be at top level (chat-style) or inside payload (BaseFeature.sendResponse style)
        const origId = originalMessageId || payload?.originalMessageId;
        if (origId && this.pendingRequests.has(origId)) {
            const { resolve } = this.pendingRequests.get(origId);
            this.pendingRequests.delete(origId);
            resolve(payload);
            return;
        }

        switch (action) {
            case 'config_changed':
                // Handle config update broadcast
                this.handleConfigChanged(payload);
                return;

            default:
                console.log('[AIConfig] Unknown action:', action);
        }
    }

    /**
     * Handle config change notification from desktop
     */
    handleConfigChanged(payload) {
        this.isConfigured = payload.isConfigured || false;
        this.maskedKey = payload.maskedKey || null;
        this.gameSampling = payload.gameSampling || false;
        this.notifyConfigChange();
    }

    /**
     * Send a request and wait for response
     */
    async sendRequest(cmd, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.core.sendFeatureMessage({
                feature: 'ai-config',
                version: 'v1.0',
                cmd,
                payload
            });

            // Store pending request
            this.pendingRequests.set(messageId, { resolve, reject });

            // Timeout after 30 seconds (validation can take time)
            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    reject(new Error('Request timeout'));
                }
            }, 30000);
        });
    }

    /**
     * Subscribe to config changes
     * @param {Function} callback - Called with { isConfigured, maskedKey, gameSampling }
     * @returns {Function} Unsubscribe function
     */
    subscribeToConfig(callback) {
        this.configCallbacks.add(callback);
        return () => this.configCallbacks.delete(callback);
    }

    /**
     * Notify config callbacks
     */
    notifyConfigChange() {
        const config = {
            isConfigured: this.isConfigured,
            maskedKey: this.maskedKey,
            gameSampling: this.gameSampling
        };
        for (const callback of this.configCallbacks) {
            try {
                callback(config);
            } catch (error) {
                console.error('[AIConfig] Config callback error:', error);
            }
        }
    }

    /**
     * Get current configuration status
     * @returns {Promise<Object>} { success, isConfigured, maskedKey, gameSampling }
     */
    async getConfig() {
        try {
            const response = await this.sendRequest('get_config');

            if (response?.success) {
                this.isConfigured = response.isConfigured || false;
                this.maskedKey = response.maskedKey || null;
                this.gameSampling = response.gameSampling || false;
                this.notifyConfigChange();
            }

            return response;
        } catch (error) {
            console.error('[AIConfig] Error getting config:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Configure API key
     * @param {Object} config - { apiKey?, gameSampling? }
     * @returns {Promise<Object>} { success, error? }
     */
    async configure(config) {
        try {
            const response = await this.sendRequest('configure', config);

            if (response?.success) {
                if (config.apiKey) this.isConfigured = true;
                this.notifyConfigChange();
            }

            return response;
        } catch (error) {
            console.error('[AIConfig] Error configuring:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Validate an API key without saving
     * @param {string} apiKey - API key to validate
     * @returns {Promise<Object>} { valid, error? }
     */
    async validateKey(apiKey) {
        try {
            return await this.sendRequest('validate_key', { apiKey });
        } catch (error) {
            console.error('[AIConfig] Error validating key:', error);
            return { valid: false, error: error.message };
        }
    }

    /**
     * Get Ollama configuration
     */
    async getOllamaConfig() {
        try {
            return await this.sendRequest('get_ollama_config');
        } catch (error) {
            console.error('[AIConfig] Error getting Ollama config:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Set Ollama configuration
     */
    async setOllamaConfig(config) {
        try {
            return await this.sendRequest('set_ollama_config', config);
        } catch (error) {
            console.error('[AIConfig] Error setting Ollama config:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Test Ollama connectivity
     */
    async testOllama(endpoint) {
        try {
            return await this.sendRequest('test_ollama', { endpoint });
        } catch (error) {
            console.error('[AIConfig] Error testing Ollama:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get available Ollama models
     */
    async getOllamaModels() {
        try {
            return await this.sendRequest('get_ollama_models');
        } catch (error) {
            console.error('[AIConfig] Error getting Ollama models:', error);
            return { success: false, error: error.message, models: [] };
        }
    }

    /**
     * Get all provider statuses (masked keys, configured state)
     * @returns {Promise<Object>} { success, providers: { anthropic: {...}, openai: {...}, xai: {...} } }
     */
    async getProviders() {
        try {
            return await this.sendRequest('get_providers');
        } catch (error) {
            console.error('[AIConfig] Error getting providers:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Set a provider's API key
     * @param {string} provider - 'anthropic' | 'openai' | 'xai'
     * @param {string} apiKey
     */
    async setProviderKey(provider, apiKey) {
        try {
            return await this.sendRequest('set_provider_key', { provider, apiKey });
        } catch (error) {
            console.error('[AIConfig] Error setting provider key:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Validate a provider's API key
     * @param {string} provider - 'anthropic' | 'openai' | 'xai'
     * @param {string} apiKey
     */
    async validateProviderKey(provider, apiKey) {
        try {
            return await this.sendRequest('validate_provider_key', { provider, apiKey });
        } catch (error) {
            console.error('[AIConfig] Error validating provider key:', error);
            return { valid: false, error: error.message };
        }
    }

    /**
     * Get feature assignments + defaults + model catalog
     */
    async getFeatureAssignments() {
        try {
            return await this.sendRequest('get_feature_assignments');
        } catch (error) {
            console.error('[AIConfig] Error getting feature assignments:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Set a feature's provider/model assignment
     * @param {string} featureId - e.g. 'chat.main'
     * @param {{ provider: string, model: string } | null} assignment - null to clear
     */
    async setFeatureAssignment(featureId, assignment) {
        try {
            return await this.sendRequest('set_feature_assignment', { featureId, assignment });
        } catch (error) {
            console.error('[AIConfig] Error setting feature assignment:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Check if API is configured
     */
    getIsConfigured() {
        return this.isConfigured;
    }

    async cleanup() {
        this.configCallbacks.clear();
        this.pendingRequests.clear();
    }
}
